import type {
  InterviewItem,
  InterviewItemTemplateId,
  Option,
  OptionId,
  QAState,
  ScreenerQuestionRule,
} from "@/components/interview/types";

/**
 * Result type for interview context - used during live interviews and preview (edit mode) interviewer.
 */
export interface InterviewOptionsResult {
  /** Options to display to the user (filtered based on context) */
  options: Option[];
  /** Whether this question uses carry forward */
  isCarryForward: boolean;
}

/**
 * Minimal fields needed for carry forward resolution.
 * Works with InterviewItemTemplate, CreationStateInterviewTemplate, etc.
 */
type MinimalTemplate = {
  id: InterviewItemTemplateId;
  type?: string;
  carryForwardFrom?: InterviewItemTemplateId;
  choices?: Option[];
};

/**
 * Gets raw choices from a template without carry forward resolution.
 * Use this to bypass the ESLint rule and @-deprecated warning when you need
 * direct access to template choices (e.g., in edit UI, serialization, duplication).
 *
 * Returns undefined if choices is undefined.
 * If you need an empty array fallback, use `getOptionsUnsafe(item) ?? []`.
 *
 */
export function getOptionsUnsafe<T extends { choices?: Option[] }>(
  template: T
): Option[] | undefined {
  return template.choices;
}

/**
 * Centralized utils for resolving question options across different contexts.
 * Handles carry forward chains, option filtering, and context-specific requirements.
 */
export class OptionsResolverUtils {
  /**
   * Gets the options for a template, resolving carry forward chains.
   *
   * @param template - The template to get options for
   * @param allTemplates - All templates in the survey (for resolving carry forward chains)
   * @param screenerRules - Optional screener rules. When provided, filters out rejected
   *   options since respondents who selected them would be screened out.
   */
  static getOptions(
    template: MinimalTemplate,
    allTemplates: MinimalTemplate[],
    screenerRules?: ScreenerQuestionRule[]
  ): Option[] {
    // Not a question or no carry forward - return choices as-is
    if (template.type !== "question" || !template.carryForwardFrom) {
      return template.choices ?? [];
    }

    // Follow carry forward chain, collecting rejected IDs along the way
    const { sourceTemplate, rejectedIds } = this.followCarryForwardChain(
      template.carryForwardFrom,
      allTemplates,
      screenerRules
    );

    if (!sourceTemplate) {
      return [];
    }

    const options = sourceTemplate.choices ?? [];

    // Filter out rejected options if any were collected
    if (rejectedIds && rejectedIds.size > 0) {
      return options.filter((opt) => !rejectedIds.has(opt.id));
    }

    return options;
  }

  /**
   * Resolves options for interview/live context.
   * Filters down to only what should be displayed for the current question.
   * This is done by
   * - returning all options for non-carry forward questions
   * - filtering down to the selected options of the carry forward source question (if applicable) and returning them for carry forward questions
   *
   * @param item - The current interview item (choices already resolved via sampleSurveyData)
   * @param stateHistory - Question/answer history (for finding carry forward selections)
   * @param isEditMode - Whether we're in edit mode (shows all options if no history and carry forward)
   * @returns Interview-specific options result with options and isCarryForward flag
   */
  static getDisplayOptions(
    item: InterviewItem,
    stateHistory: QAState[],
    isEditMode = false
  ): InterviewOptionsResult {
    // Not a question or no carry forward - use direct choices
    if (item.type !== "question" || !item.carryForwardFrom) {
      return {
        options: item.choices ?? [],
        isCarryForward: false,
      };
    }

    // item.choices already has resolved carry-forward options with screener filtering
    // (done in sampleSurveyData via resolveAndRandomize -> getOptions)
    const allOptions = item.choices ?? [];

    // Find answers from source question to filter down to selected options.
    // Only real submitted answers count: skipped placeholders (e.g. preview
    // seeding) carry submittedAt but no selection and must not resolve to empty.
    const sourceAnswer = stateHistory.findLast(
      (s) =>
        s.submittedAt &&
        !s.skipped &&
        s.currentTemplateId === item.carryForwardFrom &&
        (s.currentConceptId === null || s.currentConceptId === item.conceptId)
    );

    // No history - handle based on mode
    if (!sourceAnswer) {
      if (isEditMode) {
        // Edit mode: show all resolved options for preview
        return {
          options: allOptions,
          isCarryForward: true,
        };
      }
      // Live interview: return empty (will cause question to be skipped)
      return {
        options: [],
        isCarryForward: true,
      };
    }

    // Filter to only selected options, maintaining original order
    const selectedIds = new Set(sourceAnswer.selectedOptionIds);
    const options = allOptions.filter((opt) => selectedIds.has(opt.id));

    return {
      options,
      isCarryForward: true,
    };
  }

  /**
   * Follows a carry forward chain to find the ultimate source template.
   * Optionally collects rejected option IDs from screener rules along the way.
   * Returns null sourceTemplate if chain is broken or circular.
   */
  private static followCarryForwardChain(
    startTemplateId: InterviewItemTemplateId,
    allTemplates: MinimalTemplate[],
    screenerRules?: ScreenerQuestionRule[]
  ): {
    sourceTemplate: MinimalTemplate | null;
    rejectedIds: Set<OptionId> | null;
  } {
    const rejectedIds = new Set<OptionId>();
    let currentTemplateId = startTemplateId;
    const visited = new Set<InterviewItemTemplateId>();
    const MAX_DEPTH = 100;
    let depth = 0;

    while (currentTemplateId && depth < MAX_DEPTH) {
      if (visited.has(currentTemplateId)) {
        console.error(
          "Circular reference in carry forward chain",
          currentTemplateId
        );
        return { sourceTemplate: null, rejectedIds: null };
      }
      visited.add(currentTemplateId);

      const template = allTemplates.find((t) => t.id === currentTemplateId);
      if (!template || template.type !== "question") {
        return { sourceTemplate: null, rejectedIds: null };
      }

      // Collect rejected IDs from screener rules at this node
      if (screenerRules) {
        const rule = screenerRules.find(
          (r) => r.interview_question_item === currentTemplateId
        );
        rule?.rejectOptions?.forEach((id) => rejectedIds.add(id));
      }

      // If this template doesn't carry forward, it's the source
      if (!template.carryForwardFrom) {
        return { sourceTemplate: template, rejectedIds };
      }

      // Continue following the chain
      currentTemplateId = template.carryForwardFrom;
      depth++;
    }

    console.error(
      `Carry forward chain exceeds maximum depth of ${MAX_DEPTH} questions. ` +
        `This likely indicates a configuration error in question ${currentTemplateId}`
    );
    return { sourceTemplate: null, rejectedIds: null };
  }
}
